import type {
  CombinedError,
  ErrorHandler as GraphQLErrorHandler,
  TerminatedCloseEvent,
} from "@opensea/graphql"
import { makeGetClient } from "@opensea/graphql/rsc"
import { captureException, withScope } from "@sentry/nextjs"
import { trackError } from "./integrations/datadog"

type CloseEvent = TerminatedCloseEvent & {
  type: "close"
}

type Extensions = {
  classification: string
  errorType: string
  debugInfo?: {
    additionalInformation?: { "x-trace-id"?: string }
  }
}

export const extractTraceId = (error: Error): string | undefined => {
  const extensions = getExtensions(error)
  return extensions?.debugInfo?.additionalInformation?.["x-trace-id"]
}

export const getErrorClassification = (error: Error): string | undefined => {
  const extensions = getExtensions(error)
  return extensions?.classification
}

export const extractPath = (error: Error): string | undefined => {
  return JSON.stringify(getPath(error))
}

export const isValidationError = (error: Error): boolean => {
  return getErrorClassification(error) === "ValidationError"
}

const getExtensions = (error: Error): Extensions | undefined => {
  if (!isCombinedError(error)) {
    return undefined
  }
  return error.graphQLErrors[0]?.extensions as Extensions | undefined
}

const getPath = (error: Error): (string | number)[] | undefined => {
  if (!isCombinedError(error)) {
    return undefined
  }

  return error.graphQLErrors[0]?.path as (string | number)[]
}

const isCombinedError = (error: Error): error is CombinedError => {
  return error.name === "CombinedError"
}

export const errorHandler: GraphQLErrorHandler = (error, operation) => {
  void trackError(error)

  const response: Response | undefined = error.response
  const traceID = response?.headers.get("x-trace-id")

  withScope(scope => {
    const { operationName, query, variables, kind } = operation

    scope.setTags({
      "error.type": "graphql.error",
      "graphql.operation.name": operationName,
      "graphql.operation.kind": kind,
    })

    scope.setExtras({
      "graphql.query": query,
      "graphql.variables": variables,
      "x-trace-id": traceID,
      "x-trace-link": traceID ? getDatadogTraceLink(traceID) : undefined,
    })

    if (kind === "subscription") {
      const [errorID, cause] = captureSubscriptionError(error)
      // Error will fail test ~ remove this once we figure out mocking ws in tests
      if (process.env.NODE_ENV !== "test") {
        console.error(
          `${operationName} Error ID: ${errorID}`,
          JSON.stringify(cause, Object.getOwnPropertyNames(cause)),
        )
      }
    } else {
      const errorID = shouldReportOperationError(error)
        ? captureException(error)
        : "[Filtered]"

      console.error(
        `${operationName} Error ID: ${errorID}.${traceID ? ` Trace ID: ${traceID}.` : ""}${typeof window !== "undefined" ? ` href: ${window.location.href}.` : ""}`,
        JSON.stringify(
          error.networkError || error,
          Object.getOwnPropertyNames(error.networkError || error),
        ),
      )
    }
  })
}

class GraphQLSubscriptionError extends Error {
  name = "GraphQLSubscriptionError"
}

export function captureSubscriptionError(
  error: CombinedError,
): [string, Error] {
  const networkError = error.networkError

  if (!shouldReportSubscriptionError(error)) {
    return ["[Filtered]", networkError ?? error]
  }

  // these might be very hard to debug, as they are not visible within the websocket messages.
  // one example is hitting the buffer size limits, e.g. "The decoded text message was too big for the output buffer and the endpoint does not support partial messages"
  if (networkError) {
    const errorMessage = isWebsocketCloseEvent(networkError)
      ? networkError.reason || networkError.message || error.message
      : networkError.message || error.message

    const subscriptionError = new GraphQLSubscriptionError(errorMessage, {
      cause: networkError,
    })

    if (!isWebsocketCloseEvent(networkError)) {
      return [captureException(subscriptionError), networkError]
    }

    return [
      captureException(subscriptionError, {
        tags: {
          "graphql.subscription.close.wasClean": networkError.wasClean,
          "graphql.subscription.close.code": networkError.code,
        },
      }),
      networkError,
    ]
  }

  const subscriptionError = new GraphQLSubscriptionError(error.message, {
    cause: error,
  })

  return [captureException(subscriptionError), error]
}

function isWebsocketCloseEvent(event: Error): event is CloseEvent {
  const closeEvent = event as Partial<CloseEvent>
  return closeEvent.type === "close" && "reason" in event
}

function shouldReportOperationError(error: CombinedError): boolean {
  if (error.graphQLErrors && error.graphQLErrors.length > 0) {
    return shouldReportGraphqlErrors(error)
  }
  if (error.networkError) {
    return shouldReportNetworkError(error.networkError)
  }
  return true
}

/**
 * Determines if a GraphQL error should be reported based on its classification
 */
function shouldReportSubscriptionError(error: CombinedError): boolean {
  // Check if we have GraphQL errors
  if (error.graphQLErrors && error.graphQLErrors.length > 0) {
    // Use shouldReportGraphqlErrors to determine if GraphQL errors should be reported
    return shouldReportGraphqlErrors(error)
  }

  // For network-only errors, use the network error classification
  if (error.networkError) {
    return shouldReportSubscriptionNetworkError(error.networkError)
  }

  // Report other combined errors
  return true
}

/**
 * Checks if a GraphQL error is a generic "Something went wrong" error
 */
export function shouldReportGraphqlErrors(error: CombinedError): boolean {
  // Always report other GraphQL errors (business logic issues), but
  // don't report generic "Something went wrong" GraphQL errors
  if (error.graphQLErrors && error.graphQLErrors.length > 0) {
    return error.graphQLErrors.some(
      graphqlError => graphqlError.message !== "Something went wrong",
    )
  }

  return false
}

function shouldReportNetworkError(networkError: Error) {
  const message = networkError.message
  if (!message || message === "undefined" || message.trim() === "") {
    return false
  }

  // Don't report common browser/user connectivity messages
  const commonTransientMessages = [
    "network error",
    "offline",
    "no internet",
    "load failed",
    "failed to fetch",
    "NetworkError when attempting to fetch resource.",
  ]
  const lowerMessage = message.toLowerCase()
  if (commonTransientMessages.some(msg => lowerMessage.includes(msg))) {
    return false
  }

  // Report all other network errors with specific messages
  return true
}

/**
 * Classifies network errors to determine if they should be reported to Sentry.
 * Returns true for errors that should be reported (genuine issues).
 * Returns false for transient/expected errors (user connectivity issues).
 */
function shouldReportSubscriptionNetworkError(networkError: Error): boolean {
  if (!isWebsocketCloseEvent(networkError)) {
    return shouldReportNetworkError(networkError)
  }

  // Handle WebSocket close events
  const closeEvent = networkError as CloseEvent
  const normalCloseCodes = [
    1000, // Normal closure
    1001, // Going away (e.g., browser navigating away)
    1005, // No status code (expected in some cases)
  ]

  return !normalCloseCodes.includes(closeEvent.code)
}

const isError = (error: unknown): error is Error => {
  return typeof error === "object" && error !== null && "message" in error
}

export const extractGraphQlErrorMessage = (
  error: unknown,
): string | undefined => {
  if (!isError(error)) {
    return undefined
  }

  if (!isCombinedError(error)) {
    return error.message
  }

  return (
    error.networkError?.message ||
    error.graphQLErrors[0]?.message ||
    error.message
  )
}

export const { getClient } = makeGetClient(errorHandler)

export const getDatadogTraceLink = (traceID: string) => {
  return `https://app.datadoghq.com/apm/traces?traceID=${traceID}`
}
